<template>
  <div>
    <!-- topo: voltar + ações -->
    <header class="dtop">
      <router-link
        :to="{ name: 'ClinicPatients' }"
        class="backlink"
        style="margin:0"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M15 18l-6-6 6-6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        Voltar para pacientes
      </router-link>
      <div class="row" style="gap:var(--space-3)">
        <button class="btn btn--outline btn--sm" @click="sendMessage">
          Enviar mensagem
        </button>
        <router-link
          :to="{ name: 'ClinicSchedule' }"
          class="btn btn--primary btn--sm"
        >
          + Agendar consulta
        </router-link>
      </div>
    </header>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar o paciente"
        message="Não encontramos a ficha deste paciente ou ocorreu um erro. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetch">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="card" style="margin-bottom:var(--space-6)">
          <div
            class="skel-line"
            style="width:60%;height:24px;margin-bottom:16px"
          />
          <div class="skel-line" style="width:40%;height:16px" />
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:180px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- cabeçalho do paciente -->
        <div class="card crm-head">
          <div class="avatar avatar--xl" :class="patient.avatarClass">
            {{ patient.initials }}
          </div>
          <div class="pid">
            <h2>{{ patient.name }}</h2>
            <div class="sub">
              <span>CPF {{ patient.cpf || "—" }}</span>
              <span>{{ formatPhone(patient.contact) }}</span>
              <span>{{ patient.email }}</span>
            </div>
            <div class="row" style="gap:var(--space-2);margin-top:2px">
              <span class="badge" :class="`badge--${statusVariant}`">
                {{ patient.statusLabel }}
              </span>
              <span
                v-if="patient.doctor"
                class="badge badge--info badge--no-dot"
              >
                {{ patient.doctor.specialty }}
              </span>
              <span
                v-if="patient.doctor"
                class="badge badge--neutral badge--no-dot"
              >
                {{ patient.doctor.name }}
              </span>
            </div>
          </div>
          <div class="crm-lastvisit">
            <span class="crm-lastvisit__label">Última consulta</span>
            <span class="crm-lastvisit__date">
              {{ slotLabel(patient.lastAppointment) || "—" }}
            </span>
            <span v-if="lastVisitRelative" class="crm-lastvisit__rel">
              {{ lastVisitRelative }}
            </span>
          </div>
        </div>

        <!-- tabs -->
        <div class="tabs" style="margin-bottom:var(--space-6)">
          <button
            v-for="t in tabs"
            :key="t.id"
            class="tab"
            :class="{ 'tab--active': selectedTab === t.id }"
            @click="selectedTab = t.id"
          >
            {{ t.label }}
          </button>
        </div>

        <!-- PAINEL: VISÃO GERAL -->
        <div v-if="selectedTab === 'geral'" class="crm-grid">
          <div class="stack" style="gap:var(--space-6)">
            <div class="card">
              <div class="card-head">
                <h3>Dados pessoais</h3>
                <button class="btn btn--ghost btn--sm">Editar</button>
              </div>
              <div class="dl">
                <div v-for="f in personalData" :key="f.label">
                  <dt>{{ f.label }}</dt>
                  <dd>{{ f.value }}</dd>
                </div>
              </div>
            </div>

            <div class="card card--flush">
              <div class="card-head flush">
                <h3>Histórico de consultas</h3>
                <span class="badge badge--neutral badge--no-dot">
                  {{ consultHistory.length }} no total
                </span>
              </div>
              <table
                v-if="consultHistory.length"
                class="table"
                style="margin-top:var(--space-2)"
              >
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Profissional de saúde</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th class="t-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in consultHistory" :key="c.date + c.type">
                    <td class="num">{{ c.date }}</td>
                    <td>{{ c.doctor }}</td>
                    <td>{{ c.type }}</td>
                    <td>
                      <span class="badge badge--success">{{ c.status }}</span>
                    </td>
                    <td class="num t-right money">
                      {{ formatMoney(c.valueCents) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <ClinicStateBox
                v-else
                state="empty"
                title="Sem histórico"
                message="Este paciente ainda não possui consultas registradas."
              />
            </div>
          </div>

          <div class="crm-aside">
            <div class="card">
              <span class="fieldset-title">Resumo do paciente</span>
              <div
                class="mini-stats"
                style="grid-template-columns:1fr 1fr;margin-top:var(--space-3)"
              >
                <div
                  v-for="s in summaryStats"
                  :key="s.label"
                  class="mini-stat"
                  style="padding:var(--space-3)"
                >
                  <span class="v">{{ s.value }}</span>
                  <span class="l">{{ s.label }}</span>
                </div>
              </div>
            </div>
            <div class="card card--warning">
              <span class="fieldset-title">Acompanhamento</span>
              <p
                style="font-size:var(--font-size-sm);color:var(--color-gray-600);margin:var(--space-2) 0 var(--space-4);line-height:1.55"
              >
                {{ followUp }}
              </p>
              <button class="btn btn--outline btn--sm btn--block">
                Marcar retorno
              </button>
            </div>
            <div v-if="patient.doctor" class="card">
              <span class="fieldset-title">
                Profissional de saúde responsável
              </span>
              <div
                class="row"
                style="gap:var(--space-3);margin-top:var(--space-3)"
              >
                <div class="avatar avatar--md avatar--accent">
                  {{ patient.doctor.initials }}
                </div>
                <div class="stack" style="gap:1px">
                  <span
                    style="font-weight:var(--fw-bold);color:var(--color-gray-900);font-size:var(--font-size-sm)"
                  >
                    {{ patient.doctor.name }}
                  </span>
                  <span
                    style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
                  >
                    {{ patient.doctor.specialty }} · {{ patient.doctor.crm }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- PAINEL: TIMELINE -->
        <div
          v-else-if="selectedTab === 'timeline'"
          class="card"
          style="max-width:760px"
        >
          <div class="card-head">
            <h3>Linha do tempo</h3>
            <select
              class="select"
              style="width:auto;height:36px;font-size:var(--font-size-xs)"
            >
              <option>Todos os eventos</option>
              <option>Consultas</option>
              <option>Documentos</option>
              <option>Contatos</option>
            </select>
          </div>
          <div
            v-if="timelineView.length"
            class="timeline"
            style="margin-top:var(--space-5)"
          >
            <div v-for="(ev, i) in timelineView" :key="i" class="tl-item">
              <div class="tl-rail">
                <div
                  class="tl-dot"
                  :class="`tl-dot--${ev.type}`"
                  v-html="tlIcons[ev.type]"
                />
              </div>
              <div class="tl-body">
                <div class="tl-top">
                  <b>{{ ev.title }}</b>
                  <span class="tl-date">{{ ev.date }}</span>
                </div>
                <p>{{ ev.text }}</p>
              </div>
            </div>
          </div>
          <ClinicStateBox
            v-else
            state="empty"
            title="Sem eventos"
            message="Ainda não há eventos na linha do tempo deste paciente."
          />
        </div>

        <!-- PAINEL: DOCUMENTOS -->
        <div
          v-else-if="selectedTab === 'docs'"
          class="card card--flush"
          style="max-width:760px"
        >
          <div class="card-head flush">
            <h3>Documentos do paciente</h3>
            <span
              v-if="documents.length"
              class="badge badge--neutral badge--no-dot"
            >
              {{ documents.length }} arquivos
            </span>
          </div>
          <div
            v-if="documents.length"
            class="doc-files"
            style="padding:var(--space-4) var(--space-5) var(--space-5)"
          >
            <div v-for="(doc, i) in documents" :key="i" class="docfile">
              <div
                class="ic"
                :class="`ic--${doc.kind}`"
                v-html="docIcons[doc.kind]"
              />
              <div class="info">
                <b>{{ doc.title }}</b>
                <span>{{ doc.meta }}</span>
              </div>
              <div class="acts">
                <button
                  class="btn btn--ghost btn--sm"
                  :disabled="!doc.url"
                  @click="openDoc(doc)"
                >
                  Ver
                </button>
                <button
                  class="btn btn--outline btn--sm"
                  :disabled="!doc.url"
                  @click="openDoc(doc)"
                >
                  PDF
                </button>
              </div>
            </div>
          </div>
          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhum documento"
            message="Este paciente ainda não possui receitas, atestados ou exames anexados."
          />
        </div>

        <!-- PAINEL: PAGAMENTOS -->
        <div
          v-else-if="selectedTab === 'pag'"
          class="card card--flush"
          style="max-width:760px"
        >
          <div class="card-head flush">
            <h3>Pagamentos</h3>
            <span
              style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
            >
              Total pago:
              <b class="money" style="color:var(--color-gray-900)">
                {{ formatMoney(totalPaidCents) }}
              </b>
            </span>
          </div>
          <table
            v-if="payments.length"
            class="table"
            style="margin-top:var(--space-2)"
          >
            <thead>
              <tr>
                <th>Data</th>
                <th>Referente a</th>
                <th>Forma</th>
                <th>Status</th>
                <th class="t-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pay, i) in payments" :key="i">
                <td class="num">{{ pay.date }}</td>
                <td>{{ pay.reference }}</td>
                <td>{{ pay.method }}</td>
                <td>
                  <span class="badge badge--success">{{ pay.status }}</span>
                </td>
                <td class="num t-right money">
                  {{ formatMoney(pay.valueCents) }}
                </td>
              </tr>
            </tbody>
          </table>
          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhum pagamento"
            message="Este paciente ainda não possui pagamentos registrados."
          />
        </div>

        <!-- PAINEL: OBSERVAÇÕES -->
        <div
          v-else-if="selectedTab === 'obs'"
          class="card"
          style="max-width:760px"
        >
          <div class="card-head"><h3>Observações internas da clínica</h3></div>
          <div
            v-if="canEdit('pacientes')"
            class="field"
            style="margin-bottom:var(--space-5)"
          >
            <textarea
              v-model="newNote"
              class="textarea"
              placeholder="Adicionar nota interna sobre o paciente (não visível ao paciente)…"
            />
            <div
              class="row"
              style="justify-content:flex-end;margin-top:var(--space-2)"
            >
              <button
                class="btn btn--primary btn--sm"
                :disabled="savingNote"
                @click="saveNote"
              >
                Salvar nota
              </button>
            </div>
          </div>
          <div v-if="notesView.length" class="stack" style="gap:var(--space-3)">
            <div v-for="(note, i) in notesView" :key="i" class="note">
              <div>
                <div class="meta">
                  <b>{{ note.author }}</b>
                  · {{ note.meta }}
                </div>
                <p>{{ note.text }}</p>
              </div>
            </div>
          </div>
          <ClinicStateBox
            v-else
            state="empty"
            title="Sem observações"
            message="Nenhuma nota interna foi registrada para este paciente."
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import { formatMoney } from "@/data/clinica";
import { formatPhone } from "@/utils/formatPhone";
import { ClinicPatients } from "@/services/clinica";
import { slotLabel } from "@/services/clinica/helpers";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";

const TL_ICONS = {
  retorno: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" stroke-width="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" stroke-width="2"/></svg>`,
  contato: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v12H5.2L4 17.2V4z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`,
  receita: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M14 2v6h6" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`,
  consulta: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  atestado: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/></svg>`
};

const DOC_ICONS = {
  rx: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M14 2v6h6" stroke="currentColor" stroke-width="2"/></svg>`,
  at: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/></svg>`,
  ex: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 17l5-5 4 3 7-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
};

const EVENT_TITLES = {
  consulta: "Consulta realizada",
  receita: "Receita emitida",
  atestado: "Atestado emitido",
  contato: "Contato realizado",
  retorno: "Retorno agendado"
};

const STATUS_VARIANTS = {
  ativo: "success",
  sem_retorno: "warning",
  novo: "info",
  inativo: "neutral"
};

export default {
  name: "ClinicPatientProfile",
  components: { ClinicStateBox },
  mixins: [clinicCan],
  data() {
    return {
      state: "loading",
      selectedTab: "geral",
      newNote: "",
      savingNote: false,
      patient: {},
      timeline: [],
      notes: [],
      // Painéis sem endpoint dedicado (ver relatório): permanecem vazios.
      consultHistory: [],
      documents: [],
      payments: [],
      totalPaidCents: 0,
      tlIcons: TL_ICONS,
      docIcons: DOC_ICONS,
      tabs: [
        { id: "geral", label: "Visão geral" },
        { id: "timeline", label: "Timeline" },
        { id: "docs", label: "Documentos" },
        { id: "pag", label: "Pagamentos" },
        { id: "obs", label: "Observações" }
      ]
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    formatMoney,
    formatPhone,
    slotLabel,
    openDoc(doc) {
      if (doc.url) window.open(doc.url, "_blank");
    },
    // Abre o WhatsApp (wa.me) com mensagem inicial — mesmo padrão do ClinicNoReturn.vue.
    sendMessage() {
      const digits = String(this.patient.contact || "").replace(/\D/g, "");
      if (!digits) {
        this.$toast.error("Paciente sem telefone cadastrado");
        return;
      }
      const full = digits.length <= 11 ? `55${digits}` : digits;
      const first = (this.patient.name || "").split(" ")[0];
      const msg = `Olá ${first}! Aqui é da clínica.`;
      window.open(
        `https://wa.me/${full}?text=${encodeURIComponent(msg)}`,
        "_blank",
        "noopener"
      );
    },
    // Histórico de consultas a partir dos appointments do perfil, enriquecido
    // com o valor pago (quando há pagamento correspondente por appointment).
    buildConsultHistory(appointments, payments) {
      const valueByAppointment = {};
      payments.forEach(pay => {
        if (!pay.appointmentId) return;
        valueByAppointment[pay.appointmentId] =
          (valueByAppointment[pay.appointmentId] || 0) + (pay.valueCents || 0);
      });
      return appointments.map(appt => ({
        date: appt.scheduled_at
          ? String(appt.scheduled_at)
              .slice(0, 10)
              .split("-")
              .reverse()
              .join("/")
          : "—",
        doctor: appt.doctor_name || "—",
        type: (appt.unit && appt.unit.name) || "Consulta",
        status: appt.status === "confirmed" ? "Confirmada" : "Pendente",
        valueCents: valueByAppointment[appt.id] || 0
      }));
    },
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const [
          { data: patient },
          { data: timeline },
          { data: notes },
          { data: documents },
          { data: payments }
        ] = await Promise.all([
          ClinicPatients.get(this.patientId, { fail }),
          ClinicPatients.timeline(this.patientId),
          ClinicPatients.notes(this.patientId),
          ClinicPatients.documents(this.patientId).catch(() => ({ data: [] })),
          ClinicPatients.payments(this.patientId).catch(() => ({ data: [] }))
        ]);
        this.patient = patient;
        this.timeline = timeline;
        this.notes = notes;
        this.documents = documents;
        this.payments = payments;
        this.totalPaidCents = payments
          .filter(p => p.paid)
          .reduce((sum, p) => sum + (p.valueCents || 0), 0);
        this.consultHistory = this.buildConsultHistory(
          patient.appointments || [],
          payments
        );
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    async saveNote() {
      const text = this.newNote.trim();
      if (!text) {
        this.$toast.error("Escreva uma nota antes de salvar");
        return;
      }
      this.savingNote = true;
      try {
        const { data } = await ClinicPatients.addNote(this.patientId, {
          author: this.authorName,
          text
        });
        this.notes = [data, ...this.notes];
        this.newNote = "";
        this.$toast.success("Nota adicionada");
      } catch (e) {
        this.$toast.error("Não foi possível salvar a nota");
      } finally {
        this.savingNote = false;
      }
    },
    fmtDate(iso) {
      if (!iso) return "";
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    }
  },
  computed: {
    patientId() {
      return this.$route.params.id;
    },
    authorName() {
      const operator = this.$store.getters["clinica/operator"];
      return (operator && operator.name) || "Atendimento";
    },
    statusVariant() {
      return STATUS_VARIANTS[this.patient.status] || "neutral";
    },
    personalData() {
      const p = this.patient || {};
      return [
        { label: "Nome completo", value: p.name || "—" },
        { label: "Telefone", value: formatPhone(p.contact) || "—" },
        { label: "E-mail", value: p.email || "—" },
        { label: "Status", value: p.statusLabel || "—" },
        {
          label: "Última consulta",
          value: slotLabel(p.lastAppointment) || "—"
        },
        {
          label: "Profissional de saúde responsável",
          value: p.doctor ? p.doctor.name : "—"
        }
      ];
    },
    // dias desde a última consulta; null quando ausente. Valores negativos
    // (última consulta no futuro) não devem virar "há -N dias".
    daysSince() {
      const n = this.patient.daysSinceLast;
      return typeof n === "number" ? n : null;
    },
    lastVisitRelative() {
      const n = this.daysSince;
      if (n == null || !this.patient.lastAppointment) return "";
      if (n < 0) return ""; // última consulta no futuro → não é visita passada
      if (n === 0) return "hoje";
      return `há ${n} ${n === 1 ? "dia" : "dias"}`;
    },
    summaryStats() {
      const consultas = this.timeline.filter(e => e.type === "consulta").length;
      const n = this.daysSince;
      return [
        { value: this.timeline.length, label: "Eventos" },
        { value: consultas, label: "Consultas" },
        { value: this.notes.length, label: "Notas" },
        { value: n != null && n > 0 ? `${n}d` : "—", label: "Última visita" }
      ];
    },
    followUp() {
      if (this.patient.status === "sem_retorno") {
        return "Paciente sem retorno recente. Elegível para campanha de reativação.";
      }
      if (this.patient.status === "inativo") {
        return "Paciente inativo. Avaliar reengajamento e atualização de contato.";
      }
      return "Paciente em acompanhamento regular na clínica.";
    },
    timelineView() {
      return this.timeline.map(e => ({
        type: e.type,
        title: EVENT_TITLES[e.type] || "Evento",
        date: this.fmtDate(e.date),
        text: e.description
      }));
    },
    notesView() {
      return this.notes.map(n => ({
        author: n.author,
        meta: this.fmtDate(n.date),
        text: n.text
      }));
    }
  }
};
</script>
